import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  BillingMethod,
  UserRole,
  useLinkVenueBillingAccountMutation,
  useVenueUsersForBillingLinkLazyQuery,
} from '../../generated/graphql';
import { validateBillingLink } from './utils/validate-billing-link';

interface BillingLinkModalProps {
  open: boolean;
  venueId: string;
  onClose: () => void;
  // Runs after a successful link; the parent decides what happens next
  // (e.g. proceed with the plan change, or just refresh).
  onLinked: () => void | Promise<void>;
}

export const BillingLinkModal = ({
  open,
  venueId,
  onClose,
  onLinked,
}: BillingLinkModalProps) => {
  const [method, setMethod] = useState<BillingMethod>(BillingMethod.Stripe);
  const [link, setLink] = useState('');
  const [userId, setUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [linkBillingAccount] = useLinkVenueBillingAccountMutation();
  const [loadUsers, { data: usersData }] =
    useVenueUsersForBillingLinkLazyQuery();

  const users = usersData?.venueUsersForBillingLink ?? [];
  const hasOwner = users.some((u) => u.roleName === UserRole.VenueOwner);

  useEffect(() => {
    if (open && venueId && method === BillingMethod.Stripe) {
      loadUsers({ variables: { venueId } });
    }
  }, [open, venueId, method, loadUsers]);

  useEffect(() => {
    // Default to the owner once users load, but never clobber a manual selection
    // (a multi-user venue may refetch usersData after the admin picked a non-owner).
    if (users.length === 0 || userId !== '') return;
    const owner = users.find((u) => u.roleName === UserRole.VenueOwner);
    setUserId(owner?.id ?? '');
  }, [usersData]);

  const linkError = link === '' ? null : validateBillingLink(method, link);
  const stripeNeedsUser = method === BillingMethod.Stripe && userId === '';
  const canSubmit =
    link !== '' && !linkError && !stripeNeedsUser && !submitting;

  const handleClose = () => {
    setLink('');
    setUserId('');
    setMethod(BillingMethod.Stripe);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await linkBillingAccount({
        variables: {
          venueId,
          billingMethod: method,
          externalLink: link.trim(),
          userId: method === BillingMethod.Stripe ? userId : undefined,
        },
        toastConfig: {
          successText: 'Billing account linked',
          errorText:
            'Failed to link billing account. Check the link and try again.',
        },
      });

      if (result?.data?.linkVenueBillingAccount) {
        await onLinked();
        handleClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link billing account</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          label="How is this customer being billed?"
          value={method}
          onChange={(e) => setMethod(e.target.value as BillingMethod)}
          sx={{ mt: 1 }}
        >
          <MenuItem value={BillingMethod.Stripe}>Stripe (credit card)</MenuItem>
          <MenuItem value={BillingMethod.Fakturoid}>
            Fakturoid (invoice)
          </MenuItem>
        </TextField>

        <TextField
          fullWidth
          label={
            method === BillingMethod.Stripe
              ? 'Stripe customer link'
              : 'Fakturoid subject link'
          }
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={
            method === BillingMethod.Stripe
              ? 'https://dashboard.stripe.com/.../customers/cus_...'
              : 'https://app.fakturoid.cz/dishboard/subjects/...'
          }
          error={Boolean(linkError)}
          helperText={linkError ?? ' '}
          sx={{ mt: 2 }}
        />

        {method === BillingMethod.Stripe && (
          <>
            <TextField
              select
              fullWidth
              label="Billing owner (user)"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              helperText="The Stripe customer is linked to this user."
              sx={{ mt: 2 }}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {`${u.account?.firstName ?? ''} ${
                    u.account?.lastName ?? ''
                  } (${u.account?.email ?? ''}) - ${u.roleName}`}
                </MenuItem>
              ))}
            </TextField>
            {users.length > 0 && !hasOwner && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This venue has no owner. The Stripe customer will be linked to
                the selected user.
              </Alert>
            )}
            {users.length === 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                This venue has no users, so a Stripe customer cannot be linked
                (the link attaches to a user). Use the Fakturoid option instead.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          Link
        </Button>
      </DialogActions>
    </Dialog>
  );
};
